import { type RefObject, useEffect, useRef } from 'react'

/**
 * A ref to watch, the element itself for callers holding one in state or reading it off the DOM, or
 * a getter for an element that can only be found after the commit, such as one of a list of refs.
 */
export type ResizeTarget = Element | null | undefined | RefObject<Element | null> | (() => Element | null)

function resolve(target: ResizeTarget): Element | null {
	if (!target) {
		return null
	}
	if (typeof target === 'function') {
		return target()
	}
	return 'current' in target ? target.current : target
}

/**
 * Runs `onResize` whenever any of the observed elements changes size, with the entries that changed.
 *
 * Watches the elements, not the refs holding them. A RefObject's identity never changes, so an effect
 * keyed on it runs once, at a commit where the element may not be mounted yet, and never again. This
 * runs after every commit and compares the current elements against the observed ones, which costs an
 * identity check when nothing moved and re-observes when a caller mounts its element late, swaps it
 * for another, or holds it in state.
 *
 * `onResize` is read from a ref, so a caller does not have to memoize it. Callers that only need to
 * know that something changed can ignore the entries.
 */
export function useResizeObserver(
	targets: ResizeTarget | ResizeTarget[],
	onResize: (entries: ResizeObserverEntry[]) => void,
): void {
	const onResizeRef = useRef(onResize)
	onResizeRef.current = onResize
	const observedRef = useRef<Element[]>([])
	const observerRef = useRef<ResizeObserver | null>(null)

	useEffect(() => {
		const elements = (Array.isArray(targets) ? targets : [targets])
			.map(resolve)
			.filter((element): element is Element => element !== null)
		const observed = observedRef.current
		if (elements.length === observed.length && elements.every((element, i) => element === observed[i])) {
			return
		}
		observerRef.current?.disconnect()
		observedRef.current = elements
		// An element rendered into another window, such as a picture in picture window, only reports to that
		// window's observer. Absent in jsdom and older embedded browsers, where not observing is the right answer.
		const Observer = elements[0]?.ownerDocument.defaultView?.ResizeObserver ?? globalThis.ResizeObserver
		if (elements.length === 0 || typeof Observer === 'undefined') {
			observerRef.current = null
			return
		}
		const observer = new Observer(entries => onResizeRef.current(entries))
		for (const element of elements) {
			observer.observe(element)
		}
		observerRef.current = observer
	})

	useEffect(() => {
		return () => {
			observerRef.current?.disconnect()
			observerRef.current = null
			observedRef.current = []
		}
	}, [])
}
